import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Puis-je motoriser un store banne manuel existant ?",
    a: "Oui, dans la plupart des cas. Nous installons un moteur tubulaire dans l'axe d'enroulement de votre store et le relions à une commande (télécommande radio, interrupteur filaire ou application). La motorisation est possible sur la quasi-totalité des stores bannes, même anciens.",
  },
  {
    q: "Quel est l'avantage d'un store motorisé par rapport à un store manuel ?",
    a: "Le confort d'utilisation est le principal avantage : ouverture et fermeture à distance avec une télécommande. Les moteurs modernes intègrent aussi des capteurs (vent, soleil) qui automatisent la protection : le store se ferme automatiquement en cas de vent fort et peut s'ouvrir/fermer selon l'ensoleillement.",
  },
  {
    q: "Combien coûte la motorisation d'un store à Lyon ?",
    a: "Le tarif dépend du type de moteur (standard, radio, io-homecontrol), de la largeur du store et des options (capteur vent, capteur soleil, programmateur). Nous vous établissons un devis gratuit et sans engagement après diagnostic de votre store existant.",
  },
];

export default function MotorisationStore() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Motorisation de store à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Motorisation de store à Lyon : passage manuel à électrique, radio, télécommande, capteurs vent/soleil. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Réparation store à Lyon', item: 'https://depanfenetres-lyon.fr/reparation-store-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Motorisation de store', item: 'https://depanfenetres-lyon.fr/motorisation-store-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Motorisation de store à Lyon | DFPRO"
        description="Motorisez votre store manuel ou remplacez un moteur défectueux à Lyon. Radio, télécommande, capteurs vent et soleil. Devis gratuit, garantie 10 ans. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/motorisation-store-lyon/"
        ogTitle="Motorisation de store à Lyon — Radio, télécommande, capteurs | DFPRO"
        ogDescription="Passez votre store au motorisé ! Motorisation store à Lyon, devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/reparation-store-lyon/" className="hover:text-primary-500 transition-colors">Réparation store à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Motorisation de store</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/motorisation-store-hero-2026.jpg"
              alt="Motorisation de store à Lyon par DFPRO Lyon — installation d'un moteur tubulaire sur store banne"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Motorisation de store à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Passez votre store manuel en motorisé, ou remplacez votre moteur défectueux. Nos techniciens installent la motorisation adaptée à votre store à Lyon.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Motorisez votre store à Lyon : confort et automatisation
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Tourner une manivelle pour ouvrir ou fermer un store banne peut vite devenir contraignant, surtout quand on souhaite moduler la protection solaire plusieurs fois par jour. La motorisation apporte un <strong className="text-foreground-800">confort d'utilisation incomparable</strong> : une simple pression sur une télécommande ou un interrupteur suffit pour déployer ou replier votre store.
                  </p>
                  <p>
                    Chez DFPRO Lyon, nous motorisons les stores bannes, les stores extérieurs déroulants et les BSO. Le principe est simple : nous installons un <strong className="text-foreground-800">moteur tubulaire</strong> à l'intérieur du tube d'enroulement de votre store. Ce moteur est relié à une commande qui peut être une télécommande radio, un interrupteur mural filaire, ou même une <strong className="text-foreground-800">application smartphone</strong> via une box domotique (Somfy TaHoma, par exemple).
                  </p>
                  <p>
                    Les moteurs modernes que nous installons sont <strong className="text-foreground-800">silencieux, fiables et économes en énergie</strong>. Ils intègrent une détection d'obstacle qui arrête le store en cas de résistance anormale, protégeant ainsi la toile et le mécanisme. Nous posons principalement des moteurs Somfy, leader du marché, mais aussi Simu ou Bubendorff selon la compatibilité avec votre installation.
                  </p>
                  <p>
                    Pour aller plus loin dans l'automatisation, nous pouvons équiper votre store de <strong className="text-foreground-800">capteurs intelligents</strong> : un anémomètre (capteur de vent) qui replie automatiquement le store en cas de vent fort pour éviter les dommages, un capteur solaire qui gère l'ouverture et la fermeture selon l'ensoleillement, ou un programmateur horaire pour des scénarios personnalisés.
                  </p>
                  <p>
                    Nous intervenons dans tout Lyon et la Métropole de Lyon pour la motorisation de stores. Le devis est gratuit et détaillé : nous évaluons la faisabilité technique, le type de moteur le plus adapté, et vous présentons les différentes options de commande. <strong className="text-foreground-800">Garantie 10 ans</strong> sur le moteur et la pose.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services store</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Réparation store à Lyon (pilier)', href: '/reparation-store-lyon/' },
                    { label: 'Réparation store banne', href: '/reparation-store-banne-lyon/' },
                    { label: 'Store banne bloqué', href: '/store-banne-bloque-lyon/' },
                    { label: 'Réparation BSO', href: '/reparation-bso-lyon/' },
                    { label: 'Store électrique', href: '/store-electrique-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Envie de motoriser votre store à Lyon ?"
          subtitle="Appelez le 09 80 80 36 84 pour un devis gratuit. Plus de confort, moins d'effort !"
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}